import { useEffect, useState, MouseEventHandler } from 'react';

import { motion, AnimatePresence } from 'framer-motion';
import styled, { css } from 'styled-components';

import useCallbackRef from 'hooks/useCallbackRef';
import TooltipTail from 'shared/icons/tooltip_tail.svg';

import CorePortal from './CorePortal';

export type DirectionType = 'up' | 'down' | 'left' | 'right';
type EventType = 'hover' | 'mousemove';

interface ITooltip {
	_width?: string;
	event_type?: EventType;
	direction?: DirectionType;
	text?: string;
	children: string | JSX.Element | (string | JSX.Element)[];
	_disabled?: boolean;
	wrapper_width?: string;
	wrapper_height?: string;
	_margin?: string;
}

const CoreTooltip = ({
	_width,
	direction = 'down',
	text,
	children,
	event_type = 'hover',
	_disabled = false,
	wrapper_width,
	wrapper_height,
	_margin,
}: ITooltip) => {
	const [display, setDisplay] = useState(false);
	const [tooltip_dom, tooltip_ref] = useCallbackRef();
	const [position, setPosition] = useState({ top: 0, left: 0, right: 0, bottom: 0 });

	useEffect(() => {
		if (tooltip_dom === null || event_type !== 'hover') return;

		const enterEvent = (e: MouseEvent) => {
			if (event_type !== 'hover') return;

			const target = e.currentTarget as HTMLDivElement;

			const client = target.getBoundingClientRect();

			if (direction === 'down')
				setPosition(() => {
					return {
						top: client.top,
						left: client.left + target.offsetWidth / 2,
						bottom: 0,
						right: 0,
					};
				});
			else if (direction === 'up')
				setPosition(() => {
					return {
						top: 0,
						left: client.left + target.offsetWidth / 2,
						bottom: window.innerHeight - client.bottom,
						right: 0,
					};
				});
			else if (direction === 'left')
				setPosition(() => {
					return {
						top: client.top + target.offsetHeight / 2,
						left: client.right,
						bottom: 0,
						right: 0,
					};
				});
			else if (direction === 'right')
				setPosition(() => {
					return {
						top: client.top + target.offsetHeight / 2,
						left: client.left,
						bottom: 0,
						right: 0,
					};
				});
			setDisplay(true);
		};

		const leaveEvent = () => {
			setDisplay(false);
			setPosition({ top: 0, left: 0, bottom: 0, right: 0 });
		};

		tooltip_dom.addEventListener('mouseenter', enterEvent);
		tooltip_dom.addEventListener('mouseleave', leaveEvent);

		return () => {
			tooltip_dom.removeEventListener('mouseenter', enterEvent);
			tooltip_dom.removeEventListener('mouseleave', leaveEvent);
		};
	}, [tooltip_dom, event_type]);

	useEffect(() => {
		if (tooltip_dom === null || event_type !== 'mousemove') return;

		const moveEvent = (e: MouseEvent) => {
			if (!display) setDisplay(true);
			const child = tooltip_dom.children[1] as HTMLDivElement;
			if (child) {
				child.style.top = `${e.clientY - child.clientHeight / 2 - 40}px`;
				child.style.left = `${e.clientX - child.clientWidth / 2}px`;
			}
		};

		const leaveEvent = () => {
			setDisplay(false);
		};

		tooltip_dom.addEventListener('mousemove', moveEvent);
		tooltip_dom.addEventListener('mouseleave', leaveEvent);

		return () => {
			tooltip_dom.removeEventListener('mousemove', moveEvent);
			tooltip_dom.removeEventListener('mouseleave', leaveEvent);
		};
	}, [tooltip_dom, event_type]);

	const mouseEnter: MouseEventHandler<HTMLDivElement> = (e) => {
		if (event_type !== 'hover') return;

		const target = e.currentTarget;

		if (direction === 'down')
			setPosition((state) => {
				return { top: target.offsetTop, left: target.offsetLeft, bottom: 0, right: 0 };
			});

		setDisplay(true);
	};

	return (
		<TooltipWrapper
			ref={tooltip_ref}
			wrapper_width={wrapper_width}
			wrapper_height={wrapper_height}
			_margin={_margin}
		>
			{children instanceof Array ? children[0] : children}
			{!_disabled && display && (
				<AnimatePresence>
					{event_type === 'hover' && (
						<CorePortal>
							<TooltipBody
								_width={_width}
								direction={direction}
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								transition={{ ease: 'easeOut', duration: 0.1 }}
								event_type={event_type}
								position={position}
							>
								{text}
								{children instanceof Array && children[1]}
								<TooltipTail />
							</TooltipBody>
						</CorePortal>
					)}
					{event_type === 'mousemove' && (
						<TooltipBody
							_width={_width}
							direction={direction}
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ ease: 'easeOut', duration: 0.1 }}
							event_type={event_type}
							position={position}
						>
							{text}
							{children instanceof Array && children[1]}
							<TooltipTail />
						</TooltipBody>
					)}
				</AnimatePresence>
			)}
		</TooltipWrapper>
	);
};

const TooltipWrapper = styled.div<{
	wrapper_width?: string;
	wrapper_height?: string;
	_margin?: string;
}>`
	width: ${({ wrapper_width }) => wrapper_width ?? 'auto'};
	height: ${({ wrapper_height }) => wrapper_height ?? 'auto'};
	position: relative;
	${({ _margin }) => _margin && `margin: ${_margin}`}
`;

const TooltipBody = styled(motion.div)<{
	direction: DirectionType;
	event_type: EventType;
	position: { top: number; left: number; bottom: number; right: number };
	_width?: string;
}>`
	width: ${({ _width }) => (_width ? _width : 'max-content')};
	padding: 10px 16px;
	border-radius: 8px;
	white-space: pre-wrap;

	/* position: ${({ event_type }) => {
		const position_value: Record<EventType, string> = {
			hover: 'absolute',
			mousemove: 'fixed',
		};

		return position_value[event_type];
	}}; */
	position: fixed;

	${({ event_type, direction, position }) =>
		event_type === 'hover' &&
		direction === 'down' &&
		css`
			top: ${position.top - 15}px;
			left: ${position.left}px;
			transform: translateY(-100%) translateX(-50%);
		`}

	${({ event_type, direction, position }) =>
		event_type === 'hover' &&
		direction === 'up' &&
		css`
			bottom: ${position.bottom - 15}px;
			left: ${position.left}px;
			transform: translateY(100%) translateX(-50%);
		`}
	
	${({ event_type, direction, position }) =>
		event_type === 'hover' &&
		direction === 'left' &&
		css`
			top: ${position.top}px;
			left: ${position.left + 15}px;
			transform: translateY(-50%);
		`}

	${({ event_type, direction, position }) =>
		event_type === 'hover' &&
		direction === 'right' &&
		css`
			top: ${position.top}px;
			left: ${position.left - 15}px;
			transform: translateY(-50%) translateX(-100%);
		`}

	/* ${({ event_type }) =>
		event_type === 'mousemove' &&
		css`
			transform: translateY(calc(-100% - 15px)) translateX(-50%);
		`} */

	z-index: 10000000;
	/* opacity: 0; */

	transition: 0.2s;
	pointer-events: none;

	color: ${({ theme }) => theme.colors.gray.gray_white};
	background-color: ${({ theme }) => theme.colors.gray.gray80_text};
	${({ theme }) => theme.typo.body.span};

	${({ theme }) => theme.common.flexCenter};

	& > svg {
		position: absolute;
		fill: ${({ theme }) => theme.colors.gray.gray80_text};
		${({ direction }) =>
			direction === 'left' &&
			css`
				transform: rotate(-90deg);
				left: -8px;
				top: calc(50% - 6.2px);
			`}
		${({ direction }) =>
			direction === 'right' &&
			css`
				transform: rotate(90deg);
				right: -8px;
				top: calc(50% - 6.2px);
			`}
        ${({ direction }) =>
			direction === 'up' &&
			css`
				transform: rotate(0deg);
				top: -8px;
				left: calc(50% - 6.2px);
			`}
        ${({ direction }) =>
			direction === 'down' &&
			css`
				transform: rotate(180deg);
				bottom: -8px;
				left: calc(50% - 6.2px);
			`}
	}
`;

export default CoreTooltip;

// props
// 	direction: tooltip의 꼬리 부분 방향
//	text: tooltip에 들어갈 텍스트
